import { type OnboardingType } from '#app/__generated__/api.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { type UserTypeRouteBase } from '#app/types/router.js';

type useSelectedUserType = {
  getReadableTargetUserType: (args: OnboardingType) => {
    readableTargetUserType: 'Hiring' | 'Independent';
  };
  isClient: boolean;
  isContractor: boolean;
  routeHelpers: {
    selectedUserTypeRouteBase: undefined | UserTypeRouteBase;
    targetUserTypeRouteBase: undefined | UserTypeRouteBase;
  };
  selectedUserType: OnboardingType;
};

const getReadableTargetUserType = (
  userType: OnboardingType | undefined,
): {
  readableTargetUserType: 'Hiring' | 'Independent';
} => {
  switch (userType) {
    case 'CLIENT':
      return {
        readableTargetUserType: 'Independent',
      };
    case 'CONTRACTOR':
    default:
      return {
        readableTargetUserType: 'Hiring',
      };
  }
};

export const useSelectedUserType = (): useSelectedUserType => {
  const currentUser = useCurrentUser();

  const selectedUserType = currentUser?.selectedUserType ?? 'CONTRACTOR';

  const targetUserType =
    selectedUserType === 'CONTRACTOR' ? 'CLIENT' : 'CONTRACTOR';

  // If user does not have breakup, make sure we're passing null
  // into route helpers—route helpers used in nav, so this prevents
  // user from being able to navigate to a route that doesn't exist for them
  // Feature flag conditionals will be removed with feature flag,
  // so manually mapping these as undefined for now
  const getRouteHelpers = (): useSelectedUserType['routeHelpers'] => {
    return {
      selectedUserTypeRouteBase:
        selectedUserType === 'CONTRACTOR' ? 'independent' : 'client',
      targetUserTypeRouteBase:
        targetUserType === 'CONTRACTOR' ? 'client' : 'independent',
    };
  };

  const isClient = selectedUserType === 'CLIENT';
  const isContractor = selectedUserType === 'CONTRACTOR';

  return {
    getReadableTargetUserType,
    isClient,
    isContractor,
    routeHelpers: getRouteHelpers(),
    selectedUserType,
  };
};
